import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Leaf, Truck, Zap, Globe, BarChart3, CheckCircle, Info } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article9 = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white">
      <div className="container mx-auto px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Badge className="bg-green-600 text-white mr-4">
              <Leaf className="h-3 w-3 mr-1" />
              Fleet &amp; Green Tech
            </Badge>
            <span className="text-sm text-gray-600">Trans.info / CEVA Logistics</span>
          </div>
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            April 2026
            <span className="mx-2">•</span>
            <span>5 min read</span>
          </div>
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.9.title')}
          </h1>

          <img
            src="https://cdn-trans.info/uploads/2026/04/e821964380d1bd9a37e6954c54b.webp"
            alt="CEVA Logistics DAF trucks UK fleet"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.9.intro')}
            </p>

            {/* Key Stats */}
            <div className="bg-green-50 p-6 rounded-lg my-8 border border-green-200">
              <h3 className="font-bold text-green-900 mb-4">
                {language === 'ko' ? '핵심 수치' : 'Key Figures'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="text-center bg-white rounded p-3">
                  <Truck className="h-5 w-5 mx-auto mb-1 text-green-600" />
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '신규 트럭' : 'New Trucks'}</p>
                  <p className="text-3xl font-bold text-green-600">165</p>
                  <p className="text-xs text-gray-500">DAF {language === 'ko' ? '모델' : 'models'}</p>
                </div>
                <div className="text-center bg-white rounded p-3">
                  <Leaf className="h-5 w-5 mx-auto mb-1 text-emerald-600" />
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? 'CO₂ 감축' : 'CO₂ Reduction'}</p>
                  <p className="text-3xl font-bold text-emerald-600">-90%</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '경유 대비 (HVO)' : 'vs diesel (HVO)'}</p>
                </div>
                <div className="text-center bg-white rounded p-3">
                  <Zap className="h-5 w-5 mx-auto mb-1 text-blue-600" />
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '연료 유형' : 'Fuel Type'}</p>
                  <p className="text-2xl font-bold text-blue-600">HVO</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '수소화 식물성 오일' : 'Hydrotreated Vegetable Oil'}</p>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.9.section1.title')}
            </h2>
            <p className="mb-6">{t('article.9.p1')}</p>
            <p className="mb-6">{t('article.9.p2')}</p>

            {/* HVO vs Diesel Comparison */}
            <div className="bg-gray-50 p-6 rounded-lg my-8 border">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <BarChart3 className="mr-2 h-5 w-5 text-green-600" />
                {language === 'ko' ? 'HVO vs 기존 경유: 주요 비교' : 'HVO vs Conventional Diesel: Key Comparison'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-100">
                      <th className="text-left py-2 px-3">{language === 'ko' ? '특성' : 'Property'}</th>
                      <th className="text-left py-2 px-3">HVO</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '기존 경유' : 'Conventional Diesel'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      {
                        prop: language === 'ko' ? 'CO₂ 배출량' : 'CO₂ Emissions',
                        hvo: language === 'ko' ? '경유 대비 최대 -90%' : 'Up to -90% vs diesel',
                        diesel: language === 'ko' ? '기준치 (100%)' : 'Baseline (100%)',
                        highlight: true,
                      },
                      {
                        prop: language === 'ko' ? '엔진 개조 필요' : 'Engine Modification',
                        hvo: language === 'ko' ? '불필요 (드롭인)' : 'Not required (drop-in)',
                        diesel: language === 'ko' ? '해당 없음' : 'N/A',
                      },
                      {
                        prop: language === 'ko' ? '원료' : 'Feedstock',
                        hvo: language === 'ko' ? '폐지방·오일·유기물' : 'Waste fats, oils, organics',
                        diesel: language === 'ko' ? '화석 연료 (원유)' : 'Fossil fuel (crude oil)',
                      },
                      {
                        prop: language === 'ko' ? '화학적 성질' : 'Chemistry',
                        hvo: language === 'ko' ? '기존 경유와 유사' : 'Similar to conventional diesel',
                        diesel: language === 'ko' ? '탄화수소 혼합물' : 'Hydrocarbon blend',
                      },
                      {
                        prop: language === 'ko' ? '영국 인프라 가용성' : 'UK Infrastructure Availability',
                        hvo: language === 'ko' ? '점차 확대 중' : 'Increasingly available',
                        diesel: language === 'ko' ? '광범위하게 이용 가능' : 'Widely available',
                      },
                    ].map((row, i) => (
                      <tr key={i} className={`border-b ${row.highlight ? 'bg-green-50' : ''}`}>
                        <td className="py-2 px-3 font-medium">{row.prop}</td>
                        <td className={`py-2 px-3 ${row.highlight ? 'text-green-600 font-bold' : ''}`}>{row.hvo}</td>
                        <td className={`py-2 px-3 ${row.highlight ? 'text-gray-500' : ''}`}>{row.diesel}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.9.section2.title')}
            </h2>
            <p className="mb-6">{t('article.9.p3')}</p>
            <p className="mb-6">{t('article.9.p4')}</p>

            {/* Telematics Benefits */}
            <div className="bg-blue-50 p-6 rounded-lg my-8 border border-blue-200">
              <h3 className="font-bold text-blue-900 mb-4 flex items-center">
                <Info className="mr-2 h-5 w-5" />
                {language === 'ko' ? '텔레매틱스 시스템 주요 기능' : 'Telematics System: Key Capabilities'}
              </h3>
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                {[
                  language === 'ko' ? '실시간 차량 위치 추적' : 'Real-time vehicle location tracking',
                  language === 'ko' ? '운전 행동 모니터링 (안전성 향상)' : 'Driver behaviour monitoring (safety improvement)',
                  language === 'ko' ? '연료 소비 분석' : 'Fuel consumption analytics',
                  language === 'ko' ? '예측 정비 알림' : 'Predictive maintenance alerts',
                  language === 'ko' ? '경로 계획 최적화' : 'Route planning optimisation',
                  language === 'ko' ? '운영 가시성 향상' : 'Enhanced operational visibility',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white rounded p-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.9.section3.title')}
            </h2>
            <p className="mb-6">{t('article.9.p5')}</p>
            <p className="mb-6">{t('article.9.p6')}</p>

            {/* Partnership Box */}
            <div className="bg-gray-50 p-6 rounded-lg my-8 border">
              <h3 className="font-bold text-gray-900 mb-4">
                {language === 'ko' ? '도입 파트너십' : 'Fleet Introduction Partnership'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-center">
                <div className="bg-white rounded p-4 border">
                  <Truck className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                  <p className="font-bold">DAF Trucks</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '차량 제조사' : 'Vehicle Manufacturer'}</p>
                </div>
                <div className="bg-white rounded p-4 border">
                  <Globe className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <p className="font-bold">Ford &amp; Slater</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '영국 주요 DAF 딜러' : 'Major UK DAF Dealer'}</p>
                </div>
                <div className="bg-white rounded p-4 border">
                  <BarChart3 className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <p className="font-bold">TIP Group</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '리스 공급 업체' : 'Leasing Provider'}</p>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.9.section4.title')}
            </h2>
            <p className="mb-6">{t('article.9.p7')}</p>
            <p className="mb-6">{t('article.9.p8')}</p>

            {/* UK HVO Adoption Context */}
            <div className="bg-emerald-50 p-6 rounded-lg my-8 border border-emerald-200">
              <h3 className="font-bold text-emerald-900 mb-4 flex items-center">
                <Leaf className="mr-2 h-5 w-5" />
                {language === 'ko' ? '영국 도로화물 탈탄소화: HVO가 브리지 연료인 이유' : 'UK Road Freight Decarbonisation: Why HVO Is the Bridge Fuel'}
              </h3>
              <div className="space-y-3 text-sm text-gray-700">
                {[
                  {
                    label: language === 'ko' ? '드롭인 호환성' : 'Drop-in compatibility',
                    desc: language === 'ko' ? '기존 경유 엔진 개조 불필요 → 즉시 도입 가능' : 'No engine modification needed → immediate deployment',
                  },
                  {
                    label: language === 'ko' ? '인프라 장벽 낮음' : 'Low infrastructure barrier',
                    desc: language === 'ko' ? '기존 급유 인프라 활용 가능, EV 충전소 불필요' : 'Uses existing fuelling infrastructure — no charging network needed',
                  },
                  {
                    label: language === 'ko' ? '장거리 적합성' : 'Long-haul suitability',
                    desc: language === 'ko' ? '배터리 EV의 주행 거리 한계가 없는 장거리·지역 배송에 적합' : 'No range anxiety — suited for long-haul and regional distribution',
                  },
                  {
                    label: language === 'ko' ? '업계 채택 확산' : 'Broad industry adoption',
                    desc: language === 'ko' ? 'DHL, DPD 등 대형 차량 운용자들도 이미 HVO 전환 중' : 'DHL, DPD and others already transitioning fleets to HVO',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2 bg-white rounded p-3">
                    <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold">{item.label}: </span>
                      <span className="text-gray-600">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Trans.info / CEVA Logistics — 165 new DAF trucks added to UK fleet, April 2026.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a
              href="https://trans.info/en/ceva-165-daf-trucks-469024"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-green-600 hover:bg-green-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article9;
